import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import DateRangePicker from "../../Common/DateRangePicker/DateRangePicker";
import {
  diffNights,
  formatDateIL,
  formatDayNameIL,
  parseDDMMYYYYToLocal,
} from "../../../utils/DatesHelpers";
import { CVAC_GUIDED_DATES_PREFIX } from "../../../utils/customerChatGuidedDatesQuickReplies";
import "./CustomerVacationChatDateRangePicker.css";

/** Matches services/customer_chat_nlu.CVAC_PICK_DATE_RANGE_PREFIX */
const CVAC_PICK_DATE_RANGE_PREFIX = "__cvac_pick_daterange__|";

function ddmmyyyyToIso(ddmmyyyy) {
  const d = parseDDMMYYYYToLocal(ddmmyyyy);
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizePricesMap(raw) {
  if (!raw || typeof raw !== "object") return null;
  const o = {};
  for (const [k, v] of Object.entries(raw)) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) o[String(k)] = n;
  }
  return Object.keys(o).length ? o : null;
}

/**
 * Inline calendar for customer vacation chat (reuses site DateRangePicker).
 * Sends __cvac_pick_daterange__|YYYY-MM-DD|YYYY-MM-DD on submit (same protocol as quick chips).
 */
export default function CustomerVacationChatDateRangePicker({
  nightsHint = null,
  pricesByDate: pricesByDateProp = null,
  disabled,
  onComplete,
  onDateModeChoice,
}) {
  const [arrival, setArrival] = useState("");
  const [leave, setLeave] = useState("");
  const [narrow, setNarrow] = useState(false);
  const sentRef = useRef(false);
  const arrivalRef = useRef("");
  const leaveRef = useRef("");

  const normalizedPrices = useMemo(
    () => normalizePricesMap(pricesByDateProp),
    [pricesByDateProp]
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 560px)");
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const submitRange = useCallback(() => {
    if (sentRef.current || disabled) return;
    const a = arrivalRef.current;
    const b = leaveRef.current;
    const depIso = ddmmyyyyToIso(a);
    const retIso = ddmmyyyyToIso(b);
    if (!depIso || !retIso) return;
    sentRef.current = true;
    const msg = `${CVAC_PICK_DATE_RANGE_PREFIX}${depIso}|${retIso}`;
    onComplete?.(msg);
  }, [disabled, onComplete]);

  const saveArrivalDate = useCallback((v) => {
    arrivalRef.current = v;
    setArrival(v);
  }, []);

  /**
   * DateRangePicker clears return with "" after a new departure pick; when nights_hint is set,
   * immediately restore suggested return (same as site package flow).
   */
  const saveDepartureDate = useCallback(
    (v) => {
      const empty = !v || v === "";
      if (
        empty &&
        nightsHint != null &&
        Number(nightsHint) > 0 &&
        arrivalRef.current
      ) {
        const d0 = parseDDMMYYYYToLocal(arrivalRef.current);
        if (d0) {
          const endStr = formatDateIL(
            dayjs(d0).add(Number(nightsHint), "day").toDate()
          );
          leaveRef.current = endStr;
          setLeave(endStr);
          return;
        }
      }
      leaveRef.current = empty ? "" : v;
      setLeave(empty ? "" : v);
    },
    [nightsHint]
  );

  const hintNum = nightsHint != null ? Number(nightsHint) : null;
  const allowAutoSubmit = !(hintNum != null && hintNum > 0);

  useEffect(() => {
    if (disabled || sentRef.current) return;
    if (!arrival || !leave) return;
    if (!allowAutoSubmit) return;
    const t = window.setTimeout(() => submitRange(), 1400);
    return () => window.clearTimeout(t);
  }, [arrival, leave, disabled, allowAutoSubmit, submitRange]);

  const arrivalD = parseDDMMYYYYToLocal(arrival);
  const leaveD = parseDDMMYYYYToLocal(leave);
  const nightsActual =
    arrivalD && leaveD ? diffNights(arrivalD, leaveD) : null;

  const showMismatch =
    hintNum != null &&
    hintNum > 0 &&
    nightsActual != null &&
    nightsActual !== hintNum;

  let stepTitle = "בחרו תאריך יציאה";
  if (arrival && !leave) stepTitle = "בחרו תאריך חזרה";
  else if (arrival && leave) stepTitle = "הטווח מוכן — אפשר להמשיך לחיפוש";

  const depLine = arrival
    ? `${arrival}${arrivalD ? ` (${formatDayNameIL(arrivalD)})` : ""}`
    : "לא נבחר";
  const retLine = leave
    ? `${leave}${leaveD ? ` (${formatDayNameIL(leaveD)})` : ""}`
    : "לא נבחר";

  let rangeSummaryLine = null;
  if (arrival && leave && nightsActual != null && nightsActual >= 0) {
    rangeSummaryLine = `${arrival} → ${leave} (${nightsActual} ${
      nightsActual === 1 ? "לילה" : "לילות"
    })`;
  }

  const showDesktopCta = Boolean(arrival && leave && !narrow);

  return (
    <div className="hr-cvac-drp" dir="rtl">
      <p className="hr-cvac-drp__step" role="status">
        {stepTitle}
      </p>

      {onDateModeChoice ? (
        <div
          className="hr-cvac-drp__modes"
          role="group"
          aria-label="שינוי סוג בחירת תאריכים"
        >
          <button
            type="button"
            className="hr-cvac-drp__mode-btn"
            disabled={disabled}
            onClick={() =>
              onDateModeChoice(`${CVAC_GUIDED_DATES_PREFIX}flex`)
            }
          >
            גמיש
          </button>
          <button
            type="button"
            className="hr-cvac-drp__mode-btn"
            disabled={disabled}
            onClick={() =>
              onDateModeChoice(`${CVAC_GUIDED_DATES_PREFIX}pick_month`)
            }
          >
            חודש ספציפי
          </button>
        </div>
      ) : null}

      <div className="hr-cvac-drp__summary" aria-live="polite">
        <div className="hr-cvac-drp__summary-row">
          <span className="hr-cvac-drp__summary-k">תאריך יציאה:</span>{" "}
          <span className="hr-cvac-drp__summary-v">{depLine}</span>
        </div>
        <div className="hr-cvac-drp__summary-row">
          <span className="hr-cvac-drp__summary-k">תאריך חזרה:</span>{" "}
          <span className="hr-cvac-drp__summary-v">{retLine}</span>
        </div>
        {rangeSummaryLine ? (
          <div className="hr-cvac-drp__summary-range">{rangeSummaryLine}</div>
        ) : null}
      </div>

      {hintNum != null && hintNum > 0 ? (
        <p className="hr-cvac-drp__hint">
          נבחרו קודם {hintNum} לילות — תאריך החזרה מוצע אוטומטית; אפשר ללחוץ יום
          אחר לחזרה כדי לעדכן את מספר הלילות.
        </p>
      ) : (
        <p className="hr-cvac-drp__hint">
          לחצו תאריך יציאה ואז תאריך חזרה. מעבר עם העכבר מציג תצוגה מקדימה של
          הטווח.
        </p>
      )}

      {showMismatch ? (
        <p className="hr-cvac-drp__warn" role="status">
          {`נבחרו ${nightsActual} לילות; בהגדרות החיפוש צוינו ${hintNum} לילות — אפשר לדייק את תאריך החזרה.`}
        </p>
      ) : null}

      <div className="hr-cvac-drp__calendar">
        <DateRangePicker
          variant={narrow ? "phone" : "desktop"}
          calendarDateArrival={arrival}
          calendarDateLeave={leave}
          saveArrivalDate={saveArrivalDate}
          saveDepartureDate={saveDepartureDate}
          unrestrictedReturnDates
          closeOnRoundTripComplete={false}
          embedVariant="chat"
          hideHeaderDateSummary
          showPhoneCloseButton={false}
          pricesByDate={normalizedPrices}
          priceLinePrefix="החל מ־"
          priceToneBand={Boolean(normalizedPrices)}
          confirmButtonLabel={
            narrow ? "מצא חבילות בתאריכים האלה" : undefined
          }
        />
      </div>

      {showDesktopCta ? (
        <button
          type="button"
          className="hr-cvac-drp__cta"
          disabled={disabled || sentRef.current}
          onClick={() => submitRange()}
        >
          מצא חבילות בתאריכים האלה
        </button>
      ) : null}

      <p className="hr-cvac-drp__fallback">
        אם היומן לא נטען, אפשר להקליד טווח בשורת הטקסט למטה.
      </p>
    </div>
  );
}
